"use client";

import { useState, useRef, useEffect } from "react";
import { Play } from "../animate-ui/icons/play";
import { Pause } from "../animate-ui/icons/pause";

interface VideoPlayerProps {
  src?: string;
  poster?: string;
  className?: string;
  aspectRatio?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  autoPlayOnVisible?: boolean; // New prop for visibility-based autoplay
}

export default function VideoPlayer({
  src,
  poster,
  className = "",
  aspectRatio = "aspect-[0.939]",
  autoPlay = true,
  muted = true,
  loop = true,
  autoPlayOnVisible = false
}: VideoPlayerProps) {
  const hasVideoSrc = src && src.trim() !== "";
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(console.error);
    } else {
      video.pause();
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  };

  // Intersection Observer for visibility-based autoplay
  useEffect(() => {
    if (!autoPlayOnVisible || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5, // Trigger when 50% of the video is visible
        rootMargin: '0px'
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [autoPlayOnVisible]);

  // Handle autoplay based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideoSrc) return;

    if (autoPlayOnVisible) {
      if (isVisible) {
        // Resume playing when visible (either first time or returning to view)
        video.play().catch(console.error);
        setHasStartedPlaying(true);
      } else if (!isVisible && isPlaying) {
        // Pause when not visible
        video.pause();
      }
    } else if (autoPlay && !hasStartedPlaying) {
      // Traditional autoplay behavior
      video.play().catch(console.error);
      setHasStartedPlaying(true);
    }
  }, [isVisible, autoPlayOnVisible, autoPlay, hasVideoSrc, hasStartedPlaying, isPlaying]);

  const circumference = 2 * Math.PI * 14.8;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div ref={containerRef} className={`group relative ${aspectRatio} w-full overflow-hidden ${className}`}>
      {hasVideoSrc ? (
        <video
          ref={videoRef}
          className={`${aspectRatio} w-full rounded-3xl object-cover cursor-pointer bg-video-placeholder`}
          src={src}
          poster={poster}
          muted={muted}
          loop={loop}
          autoPlay={autoPlayOnVisible ? false : autoPlay}
          playsInline
          preload="metadata"
          onClick={togglePlayPause}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        <div className={`${aspectRatio} w-full rounded-3xl bg-video-placeholder flex items-center justify-center`}>
         
        </div>
      )}

      {hasVideoSrc && (
        <button
          type="button"
          onClick={togglePlayPause}
          className="absolute bottom-4 left-4 rounded-full bg-primary/60 p-2 opacity-0 transition-all duration-300 ease-in-out md:p-3 group-hover:opacity-100 hover:bg-black/30"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          <svg
            aria-label="video progress"
            role="img"
            className="-rotate-90 absolute top-0 left-0 h-full w-full"
            viewBox="0 0 32 32"
          >
            <circle
              cx="16"
              cy="16"
              r="14.8"
              fill="none"
              stroke="white"
              strokeWidth="2.3"
              className="opacity-30"
            />
            <circle
              cx="16"
              cy="16"
              r="14.8"
              fill="none"
              stroke="white"
              strokeWidth="2.3"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="opacity-90 transition-all duration-300"
              strokeLinecap="round"
            />
          </svg>

          {isPlaying ? (
            <Pause animateOnHover className="relative h-4 w-4 md:h-6 md:w-6 text-white" />
          ) : (
            <Play fill="white" animateOnHover className="relative h-4 w-4 md:h-6 md:w-6 text-white" />
          )}
        </button>
      )}
    </div>
  );
}
